(function () {
  'use strict';
  if (window.FotoMasterLeadAnalytics) return;
  window.FotoMasterLeadAnalytics = true;
  const seen = new WeakMap();
  const details = new WeakMap();
  const waits = new WeakMap();
  const diagnosticTypes = ['reach', 'interaction', 'step_view', 'contact_view', 'next_attempt',
    'submit_attempt', 'validation_error', 'dependency_error', 'request_sent', 'server_response',
    'submit_error', 'submit_wait', 'complete'];
  const stepName = value => ({ 1: 'request', 2: 'plans', 3: 'contact', Done: 'complete' })[value] || 'unknown';
  const categories = {
    field_name: ['full_name', 'email', 'phone', 'country', 'business_stage', 'urgency', 'contact_preference', 'message'],
    error_code: ['required', 'invalid_format', 'invalid_for_country', 'unavailable', 'network', 'http', 'invalid_response', 'exception', 'over_15s'],
    processing_stage: ['countries', 'phone', 'attribution', 'context', 'payload', 'request', 'response', 'confirmation', 'unknown'],
    submission_result: ['accepted_sales', 'accepted_support', 'accepted_unclassified', 'duplicate', 'acknowledged_not_accepted']
  };
  // A diagnostic watchdog reports a long wait; it never aborts, retries or
  // changes the promise, validation, consent or submission result.
  window.FotoMasterLeadObservability = Object.freeze({ phase(state, phase) {
    clearTimeout(waits.get(state));
    waits.delete(state);
    if (phase) waits.set(state, setTimeout(() => {
      waits.delete(state);
      state.diagnose?.('submit_wait', { processing_stage: phase, error_code: 'over_15s' });
    }, 15000));
  } });
  function rootFor(target) { return target?.closest?.('[data-lead-mode]'); }
  async function record(root, type, detail) {
    if (!root || !window.ShopifyConsent) return;
    // Keep the existing sales-completion series free of support and unresolved
    // requests, even if another caller dispatches an old submit event.
    if (type === 'submit' && (detail.sales_eligible !== true || detail.request_classification !== 'sales')) return;
    try {
      if (await window.ShopifyConsent.canAnalyze() !== true) return;
      // Shopify's existing Google loader may finish after the form becomes
      // visible. Give that transport time to arrive without adding a loader.
      for (let attempt = 0; typeof window.gtag !== 'function' && attempt < 20; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 250));
      }
      if (typeof window.gtag !== 'function' || await window.ShopifyConsent.canAnalyze() !== true) return;
      const state = seen.get(root) || new Set();
      const key = ['step', 'step_view'].includes(type) ? type + ':' + detail.step
        : type === 'validation_error' ? [type, detail.step, detail.field_name, detail.error_code].join(':') : type;
      if (state.has(key) && ['eligible', 'view', 'start', 'submit', 'request_submit', 'support_submit', 'unclassified_submit', 'step',
        'reach', 'interaction', 'step_view', 'contact_view', 'validation_error', 'complete'].includes(type)) return;
      // An explicit allowlist prevents contact data, message text or click IDs
      // from entering this analytics-only funnel.
      const payload = {
        send_to: 'G-PNT9FEXYZL', form_variant: root.dataset.formVariant || 'classic',
        experiment_mode: root.dataset.leadMode || 'classic',
        experiment_eligible: root.dataset.experimentEligible === 'true'
      };
      for (const name of ['step', 'intent', 'business_stage', 'timeline', 'priority', 'product', 'request_classification', 'sales_eligible']) {
        if (detail[name] !== undefined) payload[name] = String(detail[name]).slice(0, 100);
      }
      payload.form_step = stepName(detail.step);
      if (diagnosticTypes.includes(type)) {
        payload.telemetry_version = 'dropoff_v1';
        payload.form_surface = window.location?.pathname === '/pages/contact' ? 'contact_page' : 'embedded';
        for (const [name, values] of Object.entries(categories)) {
          if (values.includes(detail[name])) payload[name] = detail[name];
        }
        if (/^[1-5]\d\d$/.test(String(detail.http_status))) payload.http_status = String(detail.http_status);
      }
      window.gtag('event', 'fm_lead_form_' + type, payload);
      state.add(key);
      seen.set(root, state);
    } catch (_) {}
  }
  for (const type of ['start', 'intent', 'step', 'route', 'submit', 'request_submit', 'support_submit', 'unclassified_submit', ...diagnosticTypes]) {
    document.addEventListener('fm:lead-form-' + type, e => {
      const root = rootFor(e.target), detail = e.detail || {};
      if (root) {
        const context = { ...(details.get(root) || {}) };
        for (const name of ['step', 'intent', 'business_stage', 'timeline', 'priority', 'product']) {
          if (detail[name] !== undefined) context[name] = detail[name];
        }
        details.set(root, context);
      }
      record(root, type, detail);
    });
  }
  function init() {
    const roots = document.querySelectorAll('[data-lead-mode]');
    roots.forEach(root => record(root, 'eligible', {}));
    const visible = new Set();
    const visibleSteps = new Set();
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          visible.add(entry.target); record(rootFor(entry.target), 'view', {});
        } else visible.delete(entry.target);
      }), { threshold: 0.5 });
      roots.forEach(root => {
        // Observe the compact form heading; tall mobile forms cannot be 50% in view.
        const marker = root.querySelector('[data-fm-arm="intent_first"] .lead-v2__progress')
          || root.querySelector('[data-fm-arm="classic"] .step_wrapper_1 h1, [data-fm-arm="classic"] .step_wrapper_1 h2')
          || root.querySelector('[data-lead-view]');
        if (marker) observer.observe(marker);
      });
      const steps = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) { visibleSteps.delete(entry.target); return; }
        visibleSteps.add(entry.target);
        const root = rootFor(entry.target);
        const detail = { ...(details.get(root) || {}), step: entry.target.closest('[data-lead-step]')?.dataset.leadStep };
        record(root, 'reach', detail);
        record(root, 'step_view', detail);
        if (String(detail.step) === '3') record(root, 'contact_view', detail);
      }), { threshold: 0.5 });
      roots.forEach(root => root.querySelectorAll?.('[data-fm-arm="intent_first"] fieldset[data-lead-step] > legend')
        .forEach(marker => steps.observe(marker)));
    }
    document.addEventListener('focusin', e => {
      const root = rootFor(e.target);
      if (root?.dataset.formVariant === 'classic') record(root, 'start', {});
    });
    // Focus can be moved programmatically by the existing controller. Count a
    // real pointer/key/input interaction separately from that old start series.
    for (const type of ['pointerdown', 'keydown', 'input', 'change']) document.addEventListener(type, e => {
      if (!e.isTrusted) return;
      const root = rootFor(e.target);
      if (!root || root.dataset.formVariant !== 'intent_first' || !e.target.closest?.('[data-fm-lead-form="contact"]')
          || e.target.closest?.('[data-fm-lead-trap]')) return;
      const detail = { ...(details.get(root) || {}),
        step: e.target.closest?.('[data-lead-step]')?.dataset.leadStep || details.get(root)?.step };
      // A visitor may scroll straight to a control below its heading. A trusted
      // interaction proves reach too, so the ordered funnel cannot lose them.
      record(root, 'reach', detail);
      record(root, 'interaction', detail);
      if (String(detail.step) === '3') record(root, 'contact_view', detail);
    }, { capture: true, passive: true });
    window.ShopifyConsent?.onConsentChange?.(() => {
      roots.forEach(root => record(root, 'eligible', {}));
      visible.forEach(marker => record(rootFor(marker), 'view', {}));
      visibleSteps.forEach(marker => {
        const root = rootFor(marker), detail = { ...(details.get(root) || {}), step: marker.closest('[data-lead-step]')?.dataset.leadStep };
        record(root, 'reach', detail); record(root, 'step_view', detail);
        if (String(detail.step) === '3') record(root, 'contact_view', detail);
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
